import { ChargedOffLoanService } from '@/modules/delinquency/services/charged-off-loans.service.js';
import Logger from '@/lib/logger.js';
import { merge } from 'lodash';
import { make } from 'vuex-pathify';

const service = new ChargedOffLoanService();

// --- STATE ---
export const state = {
  chargeOffListing: [],
  chargeOffListingTotal: 0,
  currentChargeOffLoan: null
};

// --- GETTERS ---
export const getters = {
  ...make.getters(state)
};

// --- MUTATIONS ---
export const mutations = {
  ...make.mutations(state)
};

// --- ACTIONS ---

export const actions = {
  // --- List and Case Management ---
  async getChargeOffListing({ commit }, params) {
    const defaults = { page: 1, limit: 10 };
    const mergedParams = merge({}, defaults, params);
    try {
      commit('SET_CHARGE_OFF_LISTING', []);
      commit('SET_CHARGE_OFF_LISTING_TOTAL', 0);
      const { data } = await service.getChargedOffLoans(mergedParams);
      commit('SET_CHARGE_OFF_LISTING', data.loans);
      commit('SET_CHARGE_OFF_LISTING_TOTAL', data.total);
      return data;
    } catch (error) {
      Logger.error('Failed to get charged-off loans list.', error);
      throw error;
    }
  },

  async markLoansAsChargedOff() {
    try {
      const { data } = await service.markLoansAsChargedOff();
      return {
        message: `${data.recordsUpdated} new loans added to the process.`
      };
    } catch (error) {
      Logger.error('Failed to run charge-off process.', error);
      throw error;
    }
  },

  async getChargedOffLoanCaseSummary({ commit }, loanId) {
    try {
      const { data } = await service.getChargedOffLoanCaseSummary(loanId);
      state.currentChargeOffLoan = data;
      commit('SET_CURRENT_CHARGE_OFF_LOAN', data);
      return data;
    } catch (error) {
      Logger.error(`Failed to get case summary for loan ${loanId}.`, error);
      throw error;
    }
  },
  async createChargedOffCase(_, payload) {
    try {
      const { data } = await service.createChargedOffCase(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to create charge-off case.', error);
      throw error;
    }
  },

  async updateChargedOffCase(_, payload) {
    try {
      const { data } = await service.updateChargedOffCase(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to update charge-off case.', error);
      throw error;
    }
  },

  // --- Assignment ---
  async assignChargedOffLoanCase(_, payload) {
    try {
      const { data } = await service.assignChargedOffLoanCase(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to assign charge-off case.', error);
      throw error;
    }
  },

  async deassignChargedOffLoanCase(_, payload) {
    try {
      const { data } = await service.deassignChargedOffLoanCase(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to de-assign charge-off case.', error);
      throw error;
    }
  },

  // --- Comments ---
  async createChargedOffLoanCaseComment(_, payload) {
    try {
      const { data } = await service.createChargedOffLoanCaseComment(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to create comment.', error);
      throw error;
    }
  },

  async updateChargedOffLoanCaseComment(_, { id: commentId, comment }) {
    try {
      const { data } = await service.updateChargedOffLoanCaseComment(
        commentId,
        comment
      );
      return data;
    } catch (error) {
      Logger.error(`Failed to update comment ${commentId}.`, error);
      throw error;
    }
  },

  async deleteChargedOffLoanCaseComment(_, commentId) {
    try {
      const { data } = await service.deleteChargedOffLoanCaseComment(commentId);
      return data;
    } catch (error) {
      Logger.error(`Failed to delete comment ${commentId}.`, error);
      throw error;
    }
  },

  // --- External Participant Assignment ---
  async assignExternalParticipant(_, payload) {
    try {
      const { data } = await service.assignExternalParticipant(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to assign external participant.', error);
      throw error;
    }
  },

  async deassignExternalParticipant(_, payload) {
    try {
      const { data } = await service.deassignExternalParticipant(payload);
      return data;
    } catch (error) {
      Logger.error('Failed to de-assign external participant.', error);
      throw error;
    }
  }
};
